import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Factory, RefreshCw, ChevronRight, GripVertical, Building2, Package, Target, User, Filter, Ship } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const PIPELINE_STAGES = [
  { key: "raw_imported", label: "Raw Imported", color: "bg-slate-100 border-slate-300", headerBg: "bg-slate-200", textColor: "text-slate-700" },
  { key: "profile_enriched", label: "Enriched", color: "bg-blue-50 border-blue-300", headerBg: "bg-blue-100", textColor: "text-blue-700" },
  { key: "contact_identified", label: "Contact Found", color: "bg-cyan-50 border-cyan-300", headerBg: "bg-cyan-100", textColor: "text-cyan-700" },
  { key: "outreach_sent", label: "Outreach Sent", color: "bg-yellow-50 border-yellow-300", headerBg: "bg-yellow-100", textColor: "text-yellow-700" },
  { key: "response_received", label: "Response", color: "bg-orange-50 border-orange-300", headerBg: "bg-orange-100", textColor: "text-orange-700" },
  { key: "requirement_identified", label: "Requirement", color: "bg-purple-50 border-purple-300", headerBg: "bg-purple-100", textColor: "text-purple-700" },
  { key: "qualified_lead", label: "Qualified", color: "bg-green-50 border-green-300", headerBg: "bg-green-100", textColor: "text-green-700" },
  { key: "converted", label: "Converted", color: "bg-emerald-50 border-emerald-300", headerBg: "bg-emerald-100", textColor: "text-emerald-700" },
  { key: "lost", label: "Lost", color: "bg-red-50 border-red-300", headerBg: "bg-red-100", textColor: "text-red-700" }
];

// Preset shipment ranges for quick filtering
const SHIPMENT_PRESETS = [
  { label: "All", min: "", max: "" },
  { label: "Small (1-50)", min: "1", max: "50" },
  { label: "Medium (51-200)", min: "51", max: "200" },
  { label: "Large (201-500)", min: "201", max: "500" },
  { label: "Enterprise (500+)", min: "500", max: "" }
];

const ExportPipeline = () => {
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState({});
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedFactory, setDraggedFactory] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [movingFactory, setMovingFactory] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [countries, setCountries] = useState([]);
  
  // Shipment range filter
  const [minShipments, setMinShipments] = useState("");
  const [maxShipments, setMaxShipments] = useState("");
  const [activePreset, setActivePreset] = useState("All");
  const abortControllerRef = useRef(null);

  useEffect(() => {
    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    fetchPipeline();
    fetchCountries();
    
    return () => {
      // Cleanup on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [selectedCountry, minShipments, maxShipments]);

  const fetchPipeline = async () => {
    // Create new abort controller
    abortControllerRef.current = new AbortController();
    
    setLoading(true);
    try {
      const params = {};
      if (selectedCountry !== "all") params.country = selectedCountry;
      if (minShipments) params.min_shipments = minShipments;
      if (maxShipments) params.max_shipments = maxShipments;
      
      const response = await axios.get(`${API}/export/pipeline`, { 
        params,
        signal: abortControllerRef.current.signal
      });
      setPipeline(response.data.pipeline);
      setStages(response.data.stages);
    } catch (error) {
      // Don't show error for aborted/cancelled requests
      if (error.name === 'AbortError' || error.name === 'CanceledError' || axios.isCancel(error)) {
        return;
      }
      console.error("Pipeline fetch error:", error);
      toast.error("Failed to load pipeline");
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/export/factories`, { params: { limit: 1 } });
      // Get unique countries from dashboard endpoint
      const dashResponse = await axios.get(`${API}/export/dashboard`);
      if (dashResponse.data.by_country) {
        setCountries(dashResponse.data.by_country.map(c => c._id || c.country).filter(Boolean));
      }
    } catch (error) {
      console.error("Failed to fetch countries");
    }
  };

  const applyPreset = (preset) => {
    setActivePreset(preset.label);
    setMinShipments(preset.min);
    setMaxShipments(preset.max);
  };

  const clearFilters = () => {
    setSelectedCountry("all");
    setMinShipments("");
    setMaxShipments("");
    setActivePreset("All");
  };

  const hasActiveFilters = selectedCountry !== "all" || minShipments || maxShipments;

  // Drag handlers
  const handleDragStart = (e, factory, sourceStage) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", JSON.stringify({ factory, sourceStage }));
    setDraggedFactory({ ...factory, sourceStage });
    
    // Add drag image
    const dragImage = e.target.cloneNode(true);
    dragImage.style.position = "absolute";
    dragImage.style.top = "-1000px";
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const handleDragOver = (e, stageKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverStage(stageKey);
  };

  const handleDragLeave = (e) => {
    // Only clear if leaving the stage container
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverStage(null);
    }
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    setDragOverStage(null);
    
    if (!draggedFactory) return;
    
    const sourceStage = draggedFactory.sourceStage;
    
    // Don't do anything if dropped on same stage
    if (sourceStage === targetStage) {
      setDraggedFactory(null);
      return;
    }

    // Optimistic update
    const factoryId = draggedFactory.id;
    setMovingFactory(factoryId);
    
    // Update local state optimistically
    setPipeline(prev => {
      const newPipeline = { ...prev };
      
      // Remove from source
      if (newPipeline[sourceStage]) {
        newPipeline[sourceStage] = {
          ...newPipeline[sourceStage],
          count: newPipeline[sourceStage].count - 1,
          factories: newPipeline[sourceStage].factories.filter(f => f.id !== factoryId)
        };
      }
      
      // Add to target
      if (newPipeline[targetStage]) {
        newPipeline[targetStage] = {
          ...newPipeline[targetStage],
          count: newPipeline[targetStage].count + 1,
          factories: [draggedFactory, ...newPipeline[targetStage].factories]
        };
      }
      
      return newPipeline;
    });
    
    try {
      await axios.patch(`${API}/export/factories/${factoryId}/pipeline`, null, {
        params: { stage: targetStage }
      });
      
      const sourceLabel = PIPELINE_STAGES.find(s => s.key === sourceStage)?.label || sourceStage;
      const targetLabel = PIPELINE_STAGES.find(s => s.key === targetStage)?.label || targetStage;
      toast.success(`Moved to ${targetLabel}`);
    } catch (error) {
      toast.error("Failed to move factory");
      // Revert on failure
      fetchPipeline();
    } finally {
      setDraggedFactory(null);
      setMovingFactory(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedFactory(null);
    setDragOverStage(null);
  };

  // Quick move function for mobile/accessibility
  const handleQuickMove = async (factory, currentStage, newStage) => {
    if (currentStage === newStage) return;
    
    setMovingFactory(factory.id);
    
    try {
      await axios.patch(`${API}/export/factories/${factory.id}/pipeline`, null, {
        params: { stage: newStage }
      });
      toast.success(`Moved to ${PIPELINE_STAGES.find(s => s.key === newStage)?.label}`);
      fetchPipeline();
    } catch (error) {
      toast.error("Failed to move factory");
    } finally {
      setMovingFactory(null);
    }
  };

  // Calculate totals
  const totalFactories = Object.values(pipeline).reduce((sum, stage) => sum + (stage?.count || 0), 0);
  const activeStages = Object.entries(pipeline).filter(([_, data]) => data?.count > 0).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="export-pipeline-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pipeline</h1>
          <p className="text-slate-500 text-sm">
            Drag factories between stages • {totalFactories} factories in {activeStages} stages
            {hasActiveFilters && <span className="text-blue-600 ml-1">(filtered)</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Country Filter */}
          <Select value={selectedCountry} onValueChange={setSelectedCountry}>
            <SelectTrigger className="w-[160px]" data-testid="country-filter">
              <SelectValue placeholder="All Countries" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Countries</SelectItem>
              {countries.map(country => (
                <SelectItem key={country} value={country}>{country}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={fetchPipeline} className="gap-2" data-testid="refresh-pipeline-btn">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Shipment Range Filter - Critical for targeting mid/small shipments */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex items-center gap-2">
              <Ship className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-slate-700">Filter by Shipment Volume</span>
              <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
                Target mid/small for better success
              </Badge>
            </div>
            
            {/* Preset buttons */}
            <div className="flex flex-wrap gap-2">
              {SHIPMENT_PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  variant={activePreset === preset.label ? "default" : "outline"}
                  size="sm"
                  onClick={() => applyPreset(preset)}
                  className={activePreset === preset.label ? "bg-blue-600 hover:bg-blue-700" : ""}
                  data-testid={`preset-${preset.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            
            {/* Custom range inputs */}
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500 whitespace-nowrap">Custom:</Label>
              <Input
                type="number"
                placeholder="Min"
                value={minShipments}
                onChange={(e) => {
                  setMinShipments(e.target.value);
                  setActivePreset("");
                }}
                className="w-20 h-8 text-sm"
                data-testid="min-shipments-input"
              />
              <span className="text-slate-400">-</span>
              <Input
                type="number"
                placeholder="Max"
                value={maxShipments}
                onChange={(e) => {
                  setMaxShipments(e.target.value);
                  setActivePreset("");
                }}
                className="w-20 h-8 text-sm"
                data-testid="max-shipments-input"
              />
            </div>
            
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500">
                Clear filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pipeline Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
        {PIPELINE_STAGES.slice(0, 5).map(stage => {
          const count = pipeline[stage.key]?.count || 0;
          return (
            <Card key={stage.key} className={`${stage.color} border`}>
              <CardContent className="p-3">
                <p className={`text-xs font-medium ${stage.textColor} opacity-80`}>{stage.label}</p>
                <p className={`text-2xl font-bold ${stage.textColor}`}>{count}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Kanban Board */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-3 min-w-max">
          {PIPELINE_STAGES.map((stage) => {
            const stageData = pipeline[stage.key] || { count: 0, factories: [] };
            const isDragOver = dragOverStage === stage.key;
            const isSourceStage = draggedFactory?.sourceStage === stage.key;
            
            return (
              <div 
                key={stage.key} 
                className="flex-shrink-0 w-64"
                data-testid={`pipeline-stage-${stage.key}`}
              >
                {/* Stage Header */}
                <div className={`rounded-t-lg px-3 py-2.5 ${stage.headerBg} border border-b-0 ${stage.color.split(' ')[1]}`}>
                  <div className="flex items-center justify-between">
                    <h3 className={`font-semibold text-sm ${stage.textColor}`}>{stage.label}</h3>
                    <Badge 
                      variant="secondary" 
                      className={`text-xs ${stage.textColor} bg-white/50`}
                    >
                      {stageData.count}
                    </Badge>
                  </div>
                </div>
                
                {/* Stage Content - Drop Zone */}
                <div 
                  className={`
                    min-h-[450px] p-2 border border-t-0 rounded-b-lg transition-all duration-200
                    ${stage.color}
                    ${isDragOver ? 'ring-2 ring-blue-400 ring-inset bg-blue-50/50' : ''}
                    ${isSourceStage && draggedFactory ? 'opacity-50' : ''}
                  `}
                  onDragOver={(e) => handleDragOver(e, stage.key)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, stage.key)}
                  data-testid={`pipeline-dropzone-${stage.key}`}
                >
                  {/* Drop indicator */}
                  {isDragOver && !isSourceStage && (
                    <div className="bg-blue-100 border-2 border-dashed border-blue-400 rounded-lg p-3 mb-2 text-center text-blue-600 text-sm font-medium">
                      Drop here
                    </div>
                  )}
                  
                  {stageData.factories.length === 0 && !isDragOver ? (
                    <div className="text-center py-12 text-slate-400 text-sm">
                      <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      No factories
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {stageData.factories.map((factory) => {
                        const isBeingMoved = movingFactory === factory.id;
                        const isBeingDragged = draggedFactory?.id === factory.id;
                        
                        return (
                          <div
                            key={factory.id}
                            draggable={!isBeingMoved}
                            onDragStart={(e) => handleDragStart(e, factory, stage.key)}
                            onDragEnd={handleDragEnd}
                            className={`
                              bg-white rounded-lg shadow-sm border border-slate-200 
                              transition-all duration-200 group
                              ${isBeingMoved ? 'opacity-50 pointer-events-none' : ''}
                              ${isBeingDragged ? 'opacity-30 scale-95' : 'hover:shadow-md hover:border-slate-300'}
                              ${!isBeingMoved ? 'cursor-grab active:cursor-grabbing' : ''}
                            `}
                            data-testid={`pipeline-factory-${factory.id}`}
                          >
                            {/* Drag Handle & Content */}
                            <div className="p-3">
                              <div className="flex items-start gap-2">
                                {/* Drag indicator */}
                                <div className="flex-shrink-0 mt-0.5 opacity-30 group-hover:opacity-60 transition-opacity">
                                  <GripVertical className="w-4 h-4 text-slate-400" />
                                </div>
                                
                                {/* Factory Info */}
                                <div 
                                  className="flex-1 min-w-0 cursor-pointer"
                                  onClick={() => navigate(`/export/factories/${factory.id}`)}
                                >
                                  <p className="font-medium text-slate-900 text-sm truncate hover:text-blue-600 transition-colors">
                                    {factory.name}
                                  </p>
                                  <p className="text-xs text-slate-500 mt-0.5">{factory.country}</p>
                                  
                                  {/* Stats */}
                                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                                    <span className="flex items-center gap-1" title="Vendors/Suppliers">
                                      <User className="w-3 h-3" />
                                      {factory.supplier_count || 0}
                                    </span>
                                    <span className="flex items-center gap-1" title="Shipments">
                                      <Package className="w-3 h-3" />
                                      {factory.shipment_count}
                                    </span>
                                    <span className="flex items-center gap-1" title="Score">
                                      <Target className="w-3 h-3" />
                                      {factory.target_score}
                                    </span>
                                  </div>
                                  
                                  {/* HSN Codes */}
                                  {factory.hs_codes?.length > 0 && (
                                    <div className="mt-2">
                                      <p className="text-xs text-slate-400 mb-1">HSN:</p>
                                      <div className="flex flex-wrap gap-1">
                                        {factory.hs_codes.slice(0, 3).map((code, i) => (
                                          <span key={i} className="text-xs font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                            {code}
                                          </span>
                                        ))}
                                        {factory.hs_codes.length > 3 && (
                                          <span className="text-xs text-slate-400">+{factory.hs_codes.length - 3}</span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Assigned Agent */}
                                  {factory.assigned_agent_name && (
                                    <div className="flex items-center gap-1 mt-2 text-xs text-blue-600">
                                      <User className="w-3 h-3" />
                                      {factory.assigned_agent_name}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Quick Move Dropdown (visible on hover) */}
                            <div className="px-3 pb-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Select 
                                value={stage.key}
                                onValueChange={(newStage) => handleQuickMove(factory, stage.key, newStage)}
                              >
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue placeholder="Move to..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {PIPELINE_STAGES.map(s => (
                                    <SelectItem 
                                      key={s.key} 
                                      value={s.key}
                                      disabled={s.key === stage.key}
                                    >
                                      {s.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Show more indicator */}
                  {stageData.count > stageData.factories.length && (
                    <button 
                      className="w-full mt-2 py-2 text-xs text-slate-500 hover:text-slate-700 hover:bg-white/50 rounded transition-colors"
                      onClick={() => navigate(`/export/factories?stage=${stage.key}`)}
                    >
                      +{stageData.count - stageData.factories.length} more factories
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Instructions */}
      <div className="text-center text-xs text-slate-400 pb-4">
        <span className="inline-flex items-center gap-2">
          <GripVertical className="w-3 h-3" />
          Drag cards to move between stages, or use the dropdown menu
        </span>
      </div>
    </div>
  );
};

export default ExportPipeline;
